import type { TabsTriggerProps as RadixTabsTriggerProps } from '@radix-ui/react-tabs';
import { forwardRef, useEffect, useRef } from 'react';
import type { ReactNode, JSX } from 'react';
import { isNotNil } from '@wistia/type-guards';
import { useSelectedItemStyle } from '../SegmentedControl/useSelectedItemStyle';
import { mergeRefs } from '../../helpers/mergeRefs';
import { useTabsValue } from './useTabsValue';
import { StyledTabsTrigger } from './StyledTabsTrigger';

type BaseProps = Pick<RadixTabsTriggerProps, 'disabled' | 'value'>;

type IconWithLabel = {
  /**
   * The [Icon](?path=/docs/components-icon--docs) component to use, e.g. `<Icon type="favorite" />`
   */
  icon?: JSX.Element;
  /**
   * The label of the item
   */
  label?: ReactNode | string;
  'aria-label'?: never;
};

type IconWithAriaLabel = {
  /**
   * The [Icon](?path=/docs/components-icon--docs) component to use, e.g. `<Icon type="favorite" />`
   */
  icon?: JSX.Element;
  /**
   * Use this when displaying an icon without a label
   */
  'aria-label'?: string;
  label?: never;
};

type NoIcon = {
  icon?: undefined;
  /**
   * The label of the item
   */
  label?: ReactNode | string;
  'aria-label'?: never;
};

export type TabsTriggerProps = BaseProps & (IconWithAriaLabel | IconWithLabel | NoIcon);

export const TabsTrigger = forwardRef<HTMLButtonElement, TabsTriggerProps>(
  (
    { disabled = false, icon, label, 'aria-label': ariaLabel, value, ...otherProps },
    forwardedRef,
  ) => {
    const selectedValue = useTabsValue();

    const { setSelectedItemMeasurements } = useSelectedItemStyle();
    const buttonRef = useRef<HTMLButtonElement>(null);

    const combinedRef = mergeRefs([buttonRef, forwardedRef]);

    useEffect(() => {
      const buttonElem = buttonRef.current;

      if (!buttonElem) {
        return undefined;
      }

      if (selectedValue !== value) {
        return undefined;
      }

      setSelectedItemMeasurements({
        offsetLeft: buttonElem.offsetLeft,
        offsetWidth: buttonElem.offsetWidth,
        offsetHeight: buttonElem.offsetHeight,
      });

      const resizeObserver = new ResizeObserver(() => {
        setSelectedItemMeasurements({
          offsetLeft: buttonElem.offsetLeft,
          offsetWidth: buttonElem.offsetWidth,
          offsetHeight: buttonElem.offsetHeight,
        });
      });

      resizeObserver.observe(buttonElem);

      return () => {
        resizeObserver.disconnect();
      };
    }, [selectedValue, setSelectedItemMeasurements, value]);

    return (
      <StyledTabsTrigger
        // We need to spread props here so that you can wrap this in a Tooltip and
        // have the tooltip work correctly
        // https://www.radix-ui.com/primitives/docs/guides/composition#your-component-must-spread-props
        {...otherProps}
        ref={combinedRef}
        $hasLabel={isNotNil(label)}
        aria-label={isNotNil(label) ? undefined : ariaLabel}
        disabled={disabled}
        value={value}
      >
        {icon}
        {label}
      </StyledTabsTrigger>
    );
  },
);

TabsTrigger.displayName = 'TabsTrigger';
